import React from 'react';
import { Market } from '../hooks/usePredictionMarket';
import { useMarketSettlement } from '../hooks/useMarketSettlement';
import TxToast from './common/TxToast';

interface AdminSettlementPanelProps {
  market: Market;
  onSettled?: () => void;
}

/**
 * AdminSettlementPanel
 *
 * Compact settlement action panel for a single market.
 * Renders approve / reject controls and displays the transaction toast.
 * Intended to be embedded inside AdminPage or other admin UIs.
 */
export default function AdminSettlementPanel({ market, onSettled }: AdminSettlementPanelProps) {
  const { tx, resetTx, approveSettlement, rejectSettlement } = useMarketSettlement();

  const [outcome,       setOutcome]       = React.useState<boolean>(true);
  const [price,         setPrice]         = React.useState('');
  const [evidenceHash,  setEvidenceHash]  = React.useState('');
  const [rejectNote,    setRejectNote]    = React.useState('');
  const [view,          setView]          = React.useState<'approve' | 'reject' | null>(null);

  async function handleApprove(e: React.FormEvent) {
    e.preventDefault();
    const ok = await approveSettlement(market.id, outcome, price, evidenceHash);
    if (ok) {
      setView(null);
      onSettled?.();
    }
  }

  function handleReject(e: React.FormEvent) {
    e.preventDefault();
    rejectSettlement(market.id, rejectNote);
    setView(null);
    onSettled?.();
  }

  function reset() {
    setView(null);
    resetTx();
    setOutcome(true);
    setPrice('');
    setEvidenceHash('');
    setRejectNote('');
  }

  return (
    <div className="admin-settlement-panel">
      <TxToast tx={tx} onDismiss={resetTx} />

      {view === null && (
        <div className="settlement-card-actions">
          <button
            className="btn-primary"
            onClick={() => { resetTx(); setView('approve'); }}
            disabled={market.resolved}
          >
            ✅ Approve Settlement
          </button>
          <button
            className="btn-outline btn-danger-outline"
            onClick={() => { resetTx(); setView('reject'); }}
            disabled={market.resolved}
          >
            🚫 Reject
          </button>
        </div>
      )}

      {view === 'approve' && (
        <form onSubmit={handleApprove} noValidate className="settlement-form">
          <div className="form-group">
            <label className="form-label">Outcome</label>
            <div className="prediction-toggle">
              <button
                type="button"
                className={`toggle-btn toggle-yes${outcome ? ' active' : ''}`}
                onClick={() => setOutcome(true)}
              >
                YES
              </button>
              <button
                type="button"
                className={`toggle-btn toggle-no${!outcome ? ' active' : ''}`}
                onClick={() => setOutcome(false)}
              >
                NO
              </button>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor={`asp-price-${market.id}`}>
              Price (optional)
            </label>
            <input
              id={`asp-price-${market.id}`}
              className="form-input"
              type="text"
              placeholder="Settlement price in wei"
              value={price}
              onChange={e => setPrice(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor={`asp-ev-${market.id}`}>
              Evidence Hash (optional)
            </label>
            <input
              id={`asp-ev-${market.id}`}
              className="form-input"
              type="text"
              placeholder="IPFS CID or keccak256"
              value={evidenceHash}
              onChange={e => setEvidenceHash(e.target.value)}
            />
          </div>
          <div className="form-row">
            <button
              type="submit"
              className="btn-primary"
              disabled={tx.status === 'pending'}
            >
              {tx.status === 'pending' ? '⏳ Submitting…' : `Confirm ${outcome ? 'YES' : 'NO'}`}
            </button>
            <button type="button" className="btn-outline" onClick={reset}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {view === 'reject' && (
        <form onSubmit={handleReject} noValidate className="settlement-form">
          <div className="form-group">
            <label className="form-label" htmlFor={`asp-note-${market.id}`}>
              Rejection Reason
            </label>
            <textarea
              id={`asp-note-${market.id}`}
              className="form-input form-textarea"
              rows={3}
              placeholder="Explain why this settlement is being rejected…"
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
            />
          </div>
          <div className="form-row">
            <button
              type="submit"
              className="btn-danger"
              disabled={!rejectNote.trim()}
            >
              Log Rejection
            </button>
            <button type="button" className="btn-outline" onClick={reset}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
